/**
 * postPurchaseConnectBroker.ts
 *
 * Routing for the post-purchase Connect Your Broker step (Rob, 2026-10-12).
 *
 * WHY THIS EXISTS:
 * - A connected broker is what lets the AI trader work with real money, the
 *   company North Star. Rob: "go straight for the connect your broker,
 *   because that's what you need to do."
 * - Right after a BotSpot plan purchase (and after the annual offer, when it
 *   shows), the buyer lands on one focused step, then continues to wherever
 *   checkout was already sending them (challenge sign-up, survey, AI agent,
 *   marketplace strategy). Fewer hops before the first connection.
 *
 * IMPORTANT BEHAVIORS:
 * - Only BotSpot SaaS plans get the step. Bootcamp and course buyers do not.
 * - The continuation is same-origin only and can never loop back to the step.
 * - The step page itself skips straight on for buyers who already have a
 *   broker connected.
 */
import { buildAnnualOfferPath } from './annualOffer';
import { BROKER_CONNECTIONS } from './brokerConnectionCatalog';

export const CONNECT_BROKER_STEP_PATH = '/welcome/connect-broker';
const DEFAULT_CONTINUATION = '/ai-agent';
const SAAS_PLAN_SLUGS = ['light-plan', 'starter-plan', 'pro-plan', 'all-access-plan'];

function safeContinuation(value: string | null | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return DEFAULT_CONTINUATION;
  try {
    // Resolve against a fixed placeholder origin: only a same-site relative
    // path keeps it, and this works even where window.location is stubbed.
    const origin = 'https://botspot.invalid';
    const target = new URL(value, origin);
    if (target.origin !== origin || target.pathname.startsWith(CONNECT_BROKER_STEP_PATH))
      return DEFAULT_CONTINUATION;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return DEFAULT_CONTINUATION;
  }
}

/** Puts the connect step in front of `destination` for BotSpot plan buyers. */
export function buildConnectBrokerStepPath(destination: string, productSlugs: string[]): string {
  if (!productSlugs.some((slug) => SAAS_PLAN_SLUGS.includes(slug))) return destination;
  return `${CONNECT_BROKER_STEP_PATH}?${new URLSearchParams({
    continue: safeContinuation(destination),
  })}`;
}

/**
 * The one post-purchase route for every checkout surface: annual offer (for
 * monthly plans) -> Connect Your Broker (for any BotSpot plan) -> destination.
 */
export function buildPostPurchasePath(
  destination: string,
  purchasedSlugs: string[],
  annualOfferSlugs: string[],
): string {
  return buildAnnualOfferPath(
    buildConnectBrokerStepPath(destination, purchasedSlugs),
    annualOfferSlugs,
  );
}

export function connectBrokerContinuePath(search: string): string {
  return safeContinuation(new URLSearchParams(search).get('continue'));
}

export type RequestedBrokerMatch =
  | { kind: 'ibkr' }
  | { kind: 'supported'; brokerId: string }
  | { kind: 'other' };

const IBKR_NAMES = new Set([
  'interactive brokers',
  'interactivebrokers',
  'ibkr',
  'ib',
  'tws',
  'ibkr pro',
  'ibkr lite',
]);

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Reads what a buyer typed into "Which broker do you use?" so the step can
 * answer sensibly: Interactive Brokers gets its own message (it is the most
 * common ask), a broker we already support offers its connect button, and
 * anything else is a new request.
 */
export function matchRequestedBroker(typed: string): RequestedBrokerMatch {
  const name = normalize(typed);
  if (!name) return { kind: 'other' };
  if (IBKR_NAMES.has(name) || name.startsWith('interactive broker')) return { kind: 'ibkr' };
  const supported = BROKER_CONNECTIONS.find((broker: { id: string; name: string }) => {
    const brokerName = normalize(broker.name);
    return name === broker.id || name === brokerName || brokerName.split(' ').includes(name);
  });
  return supported ? { kind: 'supported', brokerId: supported.id } : { kind: 'other' };
}
